import Box from '@components/base/Box'
import { TextInput } from '@components/inputs/TextInput'
import { ReactNode } from 'react'

type RegistrationError = {
  step: string
  message: string | ReactNode
  eleId?: string
  alert?: boolean
}

type RegistrationAccountFieldsProps = {
  confirmPasswordErrorStep?: string
  errors: RegistrationError[]
  errorHandler: (
    id: string,
    alert?: boolean
  ) => (message: string, testId: string) => void
  hasAccount: boolean
  labelBgColour?: string
  nameFieldsClassName?: string
  nameInputClassName?: string
  onConfirmPasswordChange: (value: string) => void
  onEmailChange: (value: string) => void
  onFirstNameChange: (value: string) => void
  onLastNameChange: (value: string) => void
  onPasswordChange: (value: string) => void
  showConfirmPasswordField?: boolean
  showNameFields: boolean
  showPasswordField?: boolean
  signedIn: boolean
  textColour?: string
}

const nameValidations = (label: 'First Name' | 'Last Name') => ({
  minLength: {
    value: 1,
    message: `${label} must be at least 1 characters`,
  },
  maxLength: {
    value: 70,
    message: `${label} must be less than 70 characters`,
  },
  pattern: {
    value: /^[a-z ,.'-]+$/i,
    message: 'Name must consist of letters only',
  },
})

const emailValidations = {
  minLength: {
    value: 7,
    message: 'Email must be at least 7 characters',
  },
  maxLength: {
    value: 70,
    message: 'Email must be less than 70 characters',
  },
  pattern: {
    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    message: 'Email must be valid',
  },
}

const passwordValidations = {
  minLength: {
    value: 8,
    message: 'Password is too short (minimum: 8 characters)',
  },
  maxLength: {
    value: 50,
    message: 'Password is too long',
  },
}

const renderRegistrationAccountFields = ({
  confirmPasswordErrorStep = 'confirm_password',
  errors,
  errorHandler,
  hasAccount,
  labelBgColour,
  nameFieldsClassName = 'flex flex-row items-center justify-between mb-0',
  nameInputClassName,
  onConfirmPasswordChange,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  showConfirmPasswordField = !hasAccount,
  showNameFields,
  showPasswordField = true,
  signedIn,
  textColour,
}: RegistrationAccountFieldsProps) => (
  <>
    <Box show={showNameFields} className={nameFieldsClassName}>
      <TextInput
        className={nameInputClassName}
        required={!signedIn}
        validations={nameValidations('First Name')}
        onChange={onFirstNameChange}
        label="First Name"
        placeholder="Taylor"
        textColour={textColour}
        labelBgColour={labelBgColour}
      />
      <TextInput
        className={nameInputClassName}
        required={!signedIn}
        validations={nameValidations('Last Name')}
        onChange={onLastNameChange}
        label="Last Name"
        placeholder="Swift"
        textColour={textColour}
        labelBgColour={labelBgColour}
      />
    </Box>
    <TextInput
      validations={emailValidations}
      onChange={onEmailChange}
      required={!signedIn}
      error={errors.find((error) => error.eleId === 'PrimaryEmail')?.message}
      label="Email"
      type="email"
      placeholder="your@email.com"
      textColour={textColour}
      labelBgColour={labelBgColour}
    />
    <TextInput
      placeholder="Enter your password"
      type="password"
      show={showPasswordField}
      setError={errorHandler('password')}
      validations={passwordValidations}
      ignoreDefaultValidations={hasAccount}
      onChange={onPasswordChange}
      required={!signedIn}
      label="Password"
      textColour={textColour}
      labelBgColour={labelBgColour}
    />
    <TextInput
      placeholder="Confirm your password"
      type="password"
      show={showConfirmPasswordField}
      setError={errorHandler('confirm_password')}
      error={
        errors.find((error) => error.step === confirmPasswordErrorStep)?.message
      }
      validations={passwordValidations}
      ignoreDefaultValidations={hasAccount}
      onChange={onConfirmPasswordChange}
      required={!signedIn}
      label="Confirm Password"
      textColour={textColour}
      labelBgColour={labelBgColour}
    />
  </>
)

export default renderRegistrationAccountFields
